import React, { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Lightbox modal for images and videos.
 * - Click backdrop or X to close
 * - ESC to close
 * - Left / Right arrow keys to navigate
 * - Images are zoomed (object-contain) on a dark backdrop
 * - Videos open in a fullscreen-style player with controls + ESC hint
 *
 * Props:
 *   items: [{ type: 'image'|'video', src, caption? }]
 *   index: active index (number)
 *   onClose: () => void
 *   onNavigate: (newIndex) => void
 *   language: 'de' | 'en'
 *   escHint: localized "Press ESC to exit" string
 */
const Lightbox = ({ items, index, onClose, onNavigate, language = 'de', escHint = '' }) => {
  const active = items?.[index];

  const handleKey = useCallback(
    (e) => {
      if (!active) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onNavigate(index - 1);
      if (e.key === 'ArrowRight' && index < items.length - 1) onNavigate(index + 1);
    },
    [active, index, items, onClose, onNavigate]
  );

  useEffect(() => {
    if (!active) return;
    document.addEventListener('keydown', handleKey);
    // Prevent background scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [active, handleKey]);

  if (!active) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
      onClick={handleBackdrop}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8 animate-in fade-in duration-200"
    >
      {/* Close */}
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 z-10 p-2.5 rounded-full bg-[#1A2231]/80 border border-white/15 text-slate-200 hover:text-white hover:bg-[#1A2231] transition-colors"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Prev */}
      {index > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index - 1);
          }}
          aria-label="Previous"
          className="absolute left-3 sm:left-6 z-10 p-2.5 rounded-full bg-[#1A2231]/80 border border-white/15 text-slate-200 hover:text-white hover:bg-[#1A2231] transition-colors hidden sm:flex"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}

      {/* Next */}
      {index < items.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(index + 1);
          }}
          aria-label="Next"
          className="absolute right-3 sm:right-6 z-10 p-2.5 rounded-full bg-[#1A2231]/80 border border-white/15 text-slate-200 hover:text-white hover:bg-[#1A2231] transition-colors hidden sm:flex"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Content */}
      <div className="relative max-w-6xl w-full max-h-full flex flex-col items-center gap-4">
        <div className="w-full flex-1 flex items-center justify-center">
          {active.type === 'image' ? (
            <img
              src={active.src}
              alt={active.caption?.[language] || 'Media'}
              className="max-w-full max-h-[82vh] object-contain rounded-md shadow-2xl"
            />
          ) : (
            <video
              src={active.src}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-[82vh] rounded-md shadow-2xl bg-black"
            />
          )}
        </div>

        {/* Caption + counter */}
        <div className="w-full flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 text-slate-300">
          <div className="text-sm leading-relaxed max-w-4xl">
            {active.caption?.[language]}
          </div>
          <div className="flex items-center gap-4 text-[10px] mono uppercase tracking-widest text-slate-400 whitespace-nowrap">
            <span>{String(index + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}</span>
            {escHint && <span>{escHint}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lightbox;
